// src/contexts/AuthContext.js
import React, { createContext, useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { authService } from '../services/api';
import axios from 'axios';

// Get API URL from environment variable
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
    const [currentUser, setCurrentUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isInstalled, setIsInstalled] = useState(true);
    const navigate = useNavigate();

    // Check if the application is installed
    useEffect(() => {
        const initializeAuth = async () => {
            try {
                // First check installation
                const response = await axios.get(`${API_URL}/install/check`);
                setIsInstalled(response.data.installed);

                if (!response.data.installed) {
                    console.log('Navigating to install')
                    navigate('/install');
                    setLoading(false);
                    return;
                }

                // Then check authentication
                if (authService.isTokenValid()) {
                    const userData = authService.getCurrentUser();
                    setCurrentUser(userData);
                    console.log("SET CURRENT USER", userData);
                }
            } catch (error) {
                console.error('Auth initialization error:', error);
                // Handle errors appropriately
            } finally {
                setLoading(false);
            }
        };

        initializeAuth();
    }, []);

    const login = async (credentials) => {
        try {
            const result = await authService.login(credentials);
            setCurrentUser(result.data);
            toast.success('Logged in successfully');
            navigate('/dashboard');
            console.log('Navigating to dashboard');
            return result;
        } catch (error) {
            const message = error.response?.data?.message || 'Login failed';
            toast.error(message);
            throw error;
        }
    };

    const register = async (userData) => {
        try {
            const result = await authService.register(userData);
            setCurrentUser(result.data);
            toast.success('Registration successful');
            navigate('/dashboard');
            return result;
        } catch (error) {
            const message = error.response?.data?.message || 'Registration failed';
            toast.error(message);
            throw error;
        }
    };

    const logout = async () => {
        try {
            await authService.logout();
            setCurrentUser(null);
            toast.success('Logged out successfully');
            navigate('/login');
        } catch (error) {
            console.error('Logout error:', error);
            // Force logout even if API call fails
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            setCurrentUser(null);
            navigate('/login');
        }
    };

    const checkPermission = (requiredRoles) => {
        if (!currentUser || !currentUser.roles) return false;

        if (!requiredRoles || requiredRoles.length === 0) {
            return true; // No specific roles required
        }

        return currentUser.roles.some(role => requiredRoles.includes(role.name));
    };

    const hasRole = (role) => {
        if (!currentUser || !currentUser.roles || !Array.isArray(currentUser.roles)) {
            return false;
        }

        return currentUser.roles.some(userRole => userRole.name === role);
    };

    const hasAnyRole = (roles) => {
        if (!currentUser || !currentUser.roles || !Array.isArray(currentUser.roles)) {
            return false;
        }

        return currentUser.roles.some(userRole => roles.includes(userRole.name));
    };

    const value = {
        currentUser,
        loading,
        isInstalled,
        login,
        register,
        logout,
        checkPermission,
        hasRole,
        hasAnyRole,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
